import { X } from 'lucide-react'
import { createContext, useCallback, useContext, useEffect, useRef } from 'react'
import type { KeyboardEvent, MouseEvent, ReactNode, RefObject } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

import { getTabbables } from '@/lib/tabbable'

/** Tailwind max-width tokens the modal exposes. Default `lg` matches
 * the historical width; `md` is for tight confirm dialogs, `xl` / `2xl`
 * opt-in for wider content (e.g. multi-field forms with two-column
 * layouts). */
export type ModalSize = 'md' | 'lg' | 'xl' | '2xl' | '4xl' | '6xl' | '7xl'

const SIZE_CLASS: Record<ModalSize, string> = {
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
  '4xl': 'max-w-4xl',
  '6xl': 'max-w-6xl',
  '7xl': 'max-w-7xl',
}

/**
 * Where the dialog sits against the backdrop. `center` is the historical
 * (and default) behaviour; `end` pins it to the trailing edge, full
 * height, as a side sheet — the shape a settings-style panel wants when
 * changes apply live behind it and the user needs to keep seeing them.
 *
 * Both placements keep IDENTICAL semantics — same focus trap, same
 * Escape chain, same backdrop-click rule — because the difference is
 * purely where the box is drawn. A sheet with its own dismissal rules
 * would be a second component wearing this one's name.
 */
export type ModalPlacement = 'center' | 'end'

interface ModalProps {
  open: boolean
  onClose: () => void
  children: ReactNode
  /** Optional accessible label when no <Modal.Header> is rendered. */
  ariaLabel?: string
  /** Disable backdrop click-to-close. Default: false (click-outside dismisses). */
  disableBackdropClose?: boolean
  /** Width preset. Defaults to `lg` (matches the original sizing). */
  size?: ModalSize
  /** Centered dialog (default) or trailing-edge side sheet. */
  placement?: ModalPlacement
  /**
   * This dialog opens on top of another one (a confirm over an edit form).
   * Its own 40% scrim stacks with nothing, so the form behind stays nearly
   * as bright as the confirm; a heavier veil makes the confirm the only
   * thing that reads as active.
   */
  stacked?: boolean
}

/**
 * Backdrop layout per placement. `end` drops the backdrop padding on the
 * trailing edge so the sheet meets the viewport edge, and stretches the
 * dialog to full height — a side sheet floating with a 1rem gap on every
 * side reads as a misplaced dialog, not as a panel.
 */
const PLACEMENT_BACKDROP_CLASS: Record<ModalPlacement, string> = {
  center: 'items-center justify-center p-4',
  end: 'items-stretch justify-end p-0',
}

const PLACEMENT_DIALOG_CLASS: Record<ModalPlacement, string> = {
  center: 'rounded-lg',
  // `rounded-s-lg` (not `rounded-l-lg`) so an RTL locale rounds the edge
  // that actually faces the page.
  end: 'h-full rounded-s-lg',
}

// Open modals with their nesting depth. The deepest one (the latest among
// equals) owns keys that land on <body>. Depth rather than mount order alone,
// because a nested modal that mounts together with its parent runs its effect
// first.
const openModalStack: Array<{ ref: RefObject<HTMLDivElement | null>; depth: number }> = []
const ModalDepthContext = createContext(0)

function topmostModal() {
  let top: (typeof openModalStack)[number] | undefined
  for (const entry of openModalStack) if (!top || entry.depth >= top.depth) top = entry
  return top
}

function ModalRoot({
  open,
  onClose,
  children,
  ariaLabel,
  disableBackdropClose,
  size = 'lg',
  placement = 'center',
  stacked = false,
}: ModalProps) {
  const dialogRef = useRef<HTMLDivElement | null>(null)
  const depth = useContext(ModalDepthContext) + 1
  const previouslyFocusedRef = useRef<HTMLElement | null>(null)

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Escape') {
        // Something deeper already claimed this Escape (a field cancelling
        // its edit, a listbox closing, a ProseMirror keymap). The innermost
        // open thing wins; the dialog closes only on an Escape nothing
        // handled. The nested-modal hand-off below still works because an
        // inner dialog stops propagation before this handler ever runs.
        if (event.defaultPrevented) return
        // `preventDefault` flips `event.defaultPrevented` so the
        // document-level Escape listener installed by an OUTER `<Modal>`
        // skips itself (it gates on `!event.defaultPrevented`). Without
        // this, ESC inside a nested-modal confirm dialog closes BOTH
        // the inner and outer modals. R-1 caught this in Phase 3d review.
        event.preventDefault()
        event.stopPropagation()
        onClose()
        return
      }
      if (event.key !== 'Tab' || !dialogRef.current) return
      // A portalled child (an editor popover, a nested dialog) bubbles its
      // keydown through the React tree, so a Tab it already routed itself
      // must not be re-routed here.
      if (event.defaultPrevented) return

      const dialog = dialogRef.current
      const focusables = getTabbables(dialog)

      const first = focusables[0]
      const last = focusables[focusables.length - 1]
      if (!first || !last) {
        event.preventDefault()
        dialog.focus()
        return
      }

      const active = document.activeElement as HTMLElement | null
      // Focus on the dialog box itself (a click on blank modal area lands
      // here) is outside the tabbable list: native Tab / Shift+Tab from it
      // walks into the page behind, so it wraps like the first / last edge.
      // Focus outside this dialog's DOM is left alone — it only reaches this
      // handler through a portalled child (or a nested dialog), which owns its
      // own Tab, and stealing it would break both.
      // Focus that fell to <body> (its element unmounted) is treated like the
      // box itself, so the next Tab lands back inside rather than behind.
      const onBody = !active || active === document.body
      if (!onBody && !dialog.contains(active)) return
      const onBox = onBody || active === dialog

      if (event.shiftKey) {
        if (active === first || onBox) {
          event.preventDefault()
          last.focus()
        }
      } else if (active === last || onBox) {
        event.preventDefault()
        first.focus()
      }
    },
    [onClose],
  )

  // Backdrop dismiss requires a full click that both STARTS and ENDS on the
  // backdrop — not just a mouse-down. Closing on mouse-down alone accidentally
  // dismisses when a drag (e.g. text selection inside the dialog) overshoots
  // and releases on the backdrop, and it feels twitchy. We record the element
  // the press started on, then only close if the release lands on that same
  // element. Bubbled events on a parent (where target !== currentTarget) are
  // ignored so they don't clobber the recorded press.
  const backdropDownTargetRef = useRef<EventTarget | null>(null)

  const handleBackdropMouseDown = useCallback((event: MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) {
      backdropDownTargetRef.current = event.currentTarget
    }
  }, [])

  const handleBackdropMouseUp = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      const downTarget = backdropDownTargetRef.current
      backdropDownTargetRef.current = null
      if (disableBackdropClose) return
      if (event.target === event.currentTarget && event.target === downTarget) {
        onClose()
      }
    },
    [disableBackdropClose, onClose],
  )

  // Focus management: capture opener, focus inside, restore on close/unmount.
  useEffect(() => {
    if (!open) return
    previouslyFocusedRef.current = document.activeElement as HTMLElement | null

    const id = window.setTimeout(() => {
      if (!dialogRef.current) return
      const firstFocusable = getTabbables(dialogRef.current)[0]
      if (firstFocusable) {
        firstFocusable.focus()
      } else {
        dialogRef.current.focus()
      }
    }, 0)

    return () => {
      window.clearTimeout(id)
      const opener = previouslyFocusedRef.current
      if (opener && typeof opener.focus === 'function') {
        opener.focus()
      }
    }
  }, [open])

  // Lock body scroll while open.
  useEffect(() => {
    if (!open) return
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previous
    }
  }, [open])

  // A keydown while focus sits on <body> (the focused element unmounted)
  // targets the body, so it never reaches the dialog's own handler and the
  // browser would Tab into the page behind. The topmost open modal routes it
  // back inside: Tab to its first stop, Shift+Tab to its last.
  useEffect(() => {
    if (!open) return
    const entry = { ref: dialogRef, depth }
    openModalStack.push(entry)
    const onDocTab = (event: globalThis.KeyboardEvent) => {
      if (event.key !== 'Tab' || event.defaultPrevented) return
      if (topmostModal() !== entry) return
      const active = document.activeElement
      if (active && active !== document.body) return
      const dialog = entry.ref.current
      if (!dialog) return
      const focusables = getTabbables(dialog)
      const target = event.shiftKey ? focusables[focusables.length - 1] : focusables[0]
      event.preventDefault()
      ;(target ?? dialog).focus()
    }
    document.addEventListener('keydown', onDocTab)
    return () => {
      document.removeEventListener('keydown', onDocTab)
      const at = openModalStack.lastIndexOf(entry)
      if (at !== -1) openModalStack.splice(at, 1)
    }
  }, [open, depth])

  // Document-level Escape handler. The dialog's own onKeyDown doesn't fire
  // when focus is inside a contenteditable child like TipTap's editor — the
  // contenteditable region intercepts the keydown and React's bubbling stops
  // before the dialog's handler runs. The document listener is the reliable
  // fallback. Skip regular form fields (input/textarea/select) — those have
  // their own field-level Escape handlers (e.g. EditableField cancels the
  // edit) that should run without the modal closing on top of them.
  useEffect(() => {
    if (!open) return
    const onDocKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return
      const target = event.target as HTMLElement | null
      const tag = target?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return
      onClose()
    }
    document.addEventListener('keydown', onDocKeyDown)
    return () => {
      document.removeEventListener('keydown', onDocKeyDown)
    }
  }, [open, onClose])

  if (!open) return null

  return createPortal(
    <div
      // Backdrop: the per-theme `--color-scrim` token, always a DARK veil.
      // This was `bg-fg/40`, which is right on Default (fg is near-black)
      // and wrong on both dark themes (fg is near-white), where it washed
      // the page behind the dialog LIGHTER and erased the theme while a
      // modal was open — the UI review measured oklab(0.93/0.4) on
      // Obsidian. A dialog's scrim darkens in every theme (Material,
      // macOS, Linear, Slack); the token exists so no surface has to
      // re-derive that.
      className={`fixed inset-0 z-50 flex ${stacked ? 'bg-scrim/70' : 'bg-scrim/40'} transition-opacity ${PLACEMENT_BACKDROP_CLASS[placement]}`}
      onMouseDown={handleBackdropMouseDown}
      onMouseUp={handleBackdropMouseUp}
    >
      <div
        ref={dialogRef}
        // biome-ignore lint/a11y/useSemanticElements: native <dialog> has divergent browser behavior; ARIA dialog pattern is intentional here.
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        tabIndex={-1}
        onKeyDown={handleKeyDown}
        // `border-border` (a full `border`, or `border-s` for the trailing
        // `end` sheet whose other edges are viewport-flush) alongside the shadow: `shadow-xl` is a
        // black-ink drop shadow, so on the two dark themes it is invisible
        // against the canvas and the panel has no edge at all. The 1px
        // border is what defines the panel there; on the light theme it
        // reads as the same slate hairline every other surface uses.
        className={`w-full ${SIZE_CLASS[size]} ${PLACEMENT_DIALOG_CLASS[placement]} ${placement === 'end' ? 'border-s' : 'border'} border-border bg-bg-elevated text-fg shadow-xl outline-none transition-transform`}
      >
        <ModalDepthContext.Provider value={depth}>{children}</ModalDepthContext.Provider>
      </div>
    </div>,
    document.body,
  )
}

interface SectionProps {
  children: ReactNode
  className?: string
}

interface ModalHeaderProps extends SectionProps {
  /**
   * When provided, renders an X button on the right that calls this on
   * click. Opt-in so existing modals without a close affordance stay
   * unchanged. Backdrop click + Escape still close regardless.
   */
  onClose?: () => void
}

function ModalHeader({ children, className = '', onClose }: ModalHeaderProps) {
  const { t } = useTranslation('common')
  return (
    <div
      className={`flex items-center justify-between gap-3 border-b border-border px-6 py-4 text-base font-semibold text-fg ${className}`.trim()}
    >
      <div className="min-w-0 flex-1">{children}</div>
      {onClose && (
        <button
          type="button"
          onClick={onClose}
          data-modal-dismiss
          aria-label={t('modal.closeAriaLabel')}
          className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-fg-muted transition-colors hover:bg-bg-muted hover:text-fg focus:outline-none focus:ring-2 focus:ring-accent"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      )}
    </div>
  )
}

function ModalBody({ children, className = '' }: SectionProps) {
  return <div className={`px-6 py-4 text-sm text-fg-muted ${className}`.trim()}>{children}</div>
}

function ModalFooter({ children, className = '' }: SectionProps) {
  return (
    <div
      className={`flex items-center justify-end gap-2 border-t border-border px-6 py-3 ${className}`.trim()}
    >
      {children}
    </div>
  )
}

export const Modal = Object.assign(ModalRoot, {
  Header: ModalHeader,
  Body: ModalBody,
  Footer: ModalFooter,
})
